import React from "react";
import { motion } from "framer-motion";

/** Orange Optimise product-feature section for the OrangeBySite homepage.
 *  Illustrative Orange Score visual only — never wired to real Core data.
 */

const BAND = "#0FA958";
const CATS = [
  { label: "Performance",     score: 92 },
  { label: "Mobile",          score: 84 },
  { label: "Technical",       score: 88 },
  { label: "Security Health", score: 91 },
];

export default function HomeOptimise() {
  return (
    <section id="optimise-product" data-testid="home-optimise-section"
             className="relative py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Copy */}
          <div className="lg:col-span-6">
            <div className="text-[10px] tracking-[0.32em] uppercase font-black text-black/80" data-testid="home-optimise-eyebrow">
              Orange Optimise
            </div>
            <h2 className="mt-4 font-black text-5xl sm:text-6xl leading-[1] tracking-[-0.03em] text-black" data-testid="home-optimise-headline">
              KEEP YOUR WEBSITE<br />HEALTHY. 🍊
            </h2>
            <p className="mt-6 text-[16px] md:text-[17px] max-w-xl text-black/85">
              Your website doesn't stop needing attention once it's live. Orange Optimise monitors your site's health, tracks changes and translates technical problems into clear, useful information — so you know when something needs attention.
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
              {[
                ["Scan Your Site",   "Get an Orange Score and understand your website's current health."],
                ["Monitor Changes",  "Keep track of performance, mobile, technical and other important signals."],
                ["Know What Changed","See when problems appear, scores drop or issues need your attention."],
                ["Plain English",    "No confusing technical reports. We explain what matters and why."],
              ].map(([t, d]) => (
                <div key={t} className="p-4 rounded-2xl bg-white/85 backdrop-blur-sm border border-black/10">
                  <div className="text-[10px] tracking-[0.24em] uppercase font-black text-[#FF6A00]">{t}</div>
                  <div className="mt-1 text-[13px] text-black/80 leading-snug">{d}</div>
                </div>
              ))}
            </div>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <a
                href="/optimise"
                data-testid="home-optimise-cta-primary"
                className="inline-flex items-center h-12 px-7 rounded-full bg-black text-white text-[12px] font-black uppercase tracking-[0.2em] hover:bg-[#1A1A1A] transition-colors"
              >
                Optimise my site
              </a>
              <a
                href="/optimise"
                data-testid="home-optimise-cta-secondary"
                className="inline-flex items-center h-12 px-6 rounded-full bg-white text-black text-[12px] font-black uppercase tracking-[0.2em] border-2 border-black hover:bg-black hover:text-white transition-colors"
              >
                Learn more
              </a>
            </div>
          </div>

          {/* Illustrative Orange Score card (presentational only) */}
          <div className="lg:col-span-6" data-testid="home-optimise-score-card">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-3xl bg-white border-2 border-black p-7 md:p-9 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.35)]"
              aria-hidden
            >
              <div className="text-[10px] tracking-[0.28em] uppercase font-black text-[#FF6A00]">Orange Score</div>
              <div className="mt-3 flex items-baseline gap-3">
                <div className="font-black text-7xl tracking-[-0.03em]" data-testid="home-optimise-illustrative-score">87</div>
                <div className="text-[#8A8A8A] font-black text-xl">/100</div>
                <div className="ml-2 px-3 py-1 rounded-full text-[10px] font-black tracking-[0.2em] uppercase"
                     style={{ background: `${BAND}22`, color: BAND, border: `1px solid ${BAND}44` }}>
                  GOOD HEALTH
                </div>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3">
                {CATS.map((c) => (
                  <div key={c.label} className="p-3 rounded-2xl border border-[#EAEAEA]">
                    <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#8A8A8A]">{c.label}</div>
                    <div className="mt-1 flex items-baseline gap-1.5">
                      <div className="font-black text-2xl tracking-[-0.02em]">{c.score}</div>
                      <div className="text-[10px] text-[#8A8A8A]">/100</div>
                    </div>
                    <div className="mt-2 h-1.5 rounded-full bg-[#F6F5F2] overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${c.score}%`, background: BAND }} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-5 border-t border-[#EAEAEA] flex items-center justify-between text-[11px] tracking-[0.2em] uppercase text-[#8A8A8A]">
                <span>Illustrative preview</span>
                <span className="font-black text-black">Live scan on /optimise</span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
